import React, { useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "sonner";
import { AdminShell } from "@/components/AdminShell";
import { PlayCircle, RefreshCw, RotateCcw, FileText, User, Users, Calendar, Loader2 } from "lucide-react";
import axios from "axios";
import { Panel, Eyebrow, MonoLabel } from "@/components/Panel";
import { AdminLogin, useAdminSession } from "@/pages/admin/AdminLogin";
import { apiErr, ADMIN_TOKEN_KEY } from "@/lib/authApi";
import { recapApi } from "@/lib/recapApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Recap() {
  const { user, setUser } = useAdminSession();
  if (!user) return <AdminLogin onSuccess={setUser} />;
  return <RecapConsole />;
}

function RecapConsole() {
  const [eventId, setEventId] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [members, setMembers] = useState([]);
  const [pickMember, setPickMember] = useState("");
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleHours, setScheduleHours] = useState(6);

  useEffect(() => {
    (async () => {
      try {
        const t = localStorage.getItem(ADMIN_TOKEN_KEY);
        const r = await axios.get(`${API}/admin/reconciliation/summary`, {
          params: { event_slug: "davangere-members-day-2026" }, headers: { Authorization: `Bearer ${t}` }
        });
        setEventId(r.data.event_id);
      } catch (e) { toast.error(apiErr(e)); }
    })();
  }, []);

  const load = async () => {
    if (!eventId) return;
    setBusy(true);
    try {
      const [j, m] = await Promise.all([
        recapApi.jobs(eventId),
        axios.get(`${API}/leader/members`, { params: { event_slug: "davangere-members-day-2026" } }).then((r) => r.data),
      ]);
      setJobs(j.items || []);
      setCounts(j.counts || {});
      setMembers(m.items || []);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [eventId]); // eslint-disable-line
  useEffect(() => {
    const iv = setInterval(() => { if (Object.keys(counts).length && counts.queued + counts.generating > 0) load(); }, 2500);
    return () => clearInterval(iv);
  }, [counts]); // eslint-disable-line

  const triggerAll = async () => {
    if (!window.confirm(`Enqueue a recap for every paid registration in this event?`)) return;
    try { const r = await recapApi.generate({ event_id: eventId, type: "all_members" }); toast.success(`Queued ${r.queued} member recaps`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const triggerEvent = async () => {
    try { await recapApi.generate({ event_id: eventId, type: "event" }); toast.success("Event recap queued"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const runPreview = async () => {
    if (!pickMember) return toast.error("Pick a member first");
    try { const r = await recapApi.preview(eventId, pickMember, "member"); setPreview(r.html); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const bulkRetry = async () => {
    try { const r = await recapApi.retryFailed(eventId); toast.success(`Retrying ${r.retried} failed`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const retryRow = async (id) => {
    try { await recapApi.retryOne(id); toast.success("Retry queued"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const saveSchedule = async () => {
    try {
      await recapApi.schedule({ event_slug: "davangere-members-day-2026", enabled: scheduleEnabled, recap_delay_hours: Number(scheduleHours) || 6 });
      toast.success(`Schedule ${scheduleEnabled ? "enabled" : "disabled"} · +${scheduleHours}h`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const failedCount = counts.failed || 0;
  return (
    <AdminShell>
      <Toaster theme="dark" position="top-right" />
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Eyebrow className="mt-4">Admin · Recap</Eyebrow>
            <h1 className="h-hero mt-3 text-4xl sm:text-5xl">One tap. Every recap.</h1>
          </div>
          <div className="flex items-center gap-2">
            <button className="btn-ghost" onClick={load} disabled={busy} data-testid="recap-refresh-btn"><RefreshCw size={14} /> Refresh</button>
            {failedCount > 0 && <button className="btn-ghost" onClick={bulkRetry} data-testid="recap-bulk-retry-btn"><RotateCcw size={14} /> Retry {failedCount}</button>}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="recap-counts">
          {["queued", "generating", "sent", "failed"].map((k) => (
            <Panel key={k} className="p-4">
              <div className="mono-label" style={{ color: "var(--taupe)" }}>{k}</div>
              <div className="mt-2 font-serif text-2xl" style={{ color: k === "failed" ? "var(--red-2)" : k === "sent" ? "var(--green, #7ac47a)" : "var(--text)" }}>
                {counts[k] || 0}
              </div>
            </Panel>
          ))}
          <Panel className="p-4 flex flex-col gap-2 justify-center">
            <button className="btn-red" onClick={triggerAll} disabled={!eventId} data-testid="recap-trigger-all"><Users size={14} /> All members</button>
            <button className="btn-ghost" onClick={triggerEvent} disabled={!eventId} data-testid="recap-trigger-event"><Calendar size={14} /> Event recap</button>
          </Panel>
        </div>

        <Panel className="p-6 mt-6" data-testid="recap-schedule">
          <MonoLabel>Automatic trigger</MonoLabel>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div className="inline-flex hairline">
              {[[true, "Auto-run after event"], [false, "Manual only"]].map(([v, l]) => (
                <button key={String(v)} onClick={() => setScheduleEnabled(v)}
                        data-testid={`recap-schedule-${v}`}
                        className={`px-4 py-2 text-xs mono-label ${scheduleEnabled === v ? "bg-[var(--red)] text-[var(--text)]" : "text-[var(--taupe)]"}`}>
                  {l}
                </button>
              ))}
            </div>
            <div>
              <MonoLabel>Delay hours</MonoLabel>
              <input data-testid="recap-schedule-hours" className="input-x mt-2 !font-mono !w-24" value={scheduleHours}
                      onChange={(e) => setScheduleHours(e.target.value.replace(/\D/g, ""))} />
            </div>
            <button className="btn-ghost" onClick={saveSchedule} data-testid="recap-schedule-save">Save schedule</button>
          </div>
          <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>
            When enabled, recap jobs auto-enqueue at <b>event_end + N hours</b>. Off by default; flip on the day before.
          </div>
        </Panel>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Panel className="lg:col-span-2 p-0 overflow-hidden" data-testid="recap-jobs">
            <div className="p-5 border-b border-[var(--hairline)]"><MonoLabel>Jobs · {jobs.length}</MonoLabel></div>
            <div className="max-h-[520px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="text-left mono-label" style={{ color: "var(--taupe)" }}>
                  <tr><th className="p-2">Type</th><th className="p-2">Target</th><th className="p-2">Status</th><th className="p-2">Generated</th><th className="p-2">PDF</th><th className="p-2"></th></tr>
                </thead>
                <tbody>
                  {jobs.map((j) => (
                    <tr key={j.id} className="border-t border-[var(--hairline)]" data-testid={`recap-job-${j.id.slice(0, 8)}`}>
                      <td className="p-2 mono-label">{j.type}</td>
                      <td className="p-2 font-mono text-xs">{(j.target_registration_id || "—").slice(0, 8)}</td>
                      <td className="p-2">
                        <span className="chip" style={{ color: j.status === "sent" ? "var(--green, #7ac47a)" : j.status === "failed" ? "var(--red-2)" : "var(--taupe)" }}>{j.status}</span>
                      </td>
                      <td className="p-2 mono-label" style={{ color: "var(--taupe)" }}>{j.generated_at ? j.generated_at.slice(0, 19).replace("T", " ") : "—"}</td>
                      <td className="p-2">{j.pdf_url ? <a className="btn-ghost !py-1 !px-2 text-xs" href={`${process.env.REACT_APP_BACKEND_URL}${j.pdf_url}`} target="_blank" rel="noreferrer" data-testid={`recap-pdf-${j.id.slice(0, 8)}`}><FileText size={12} /> PDF</a> : "—"}</td>
                      <td className="p-2">{j.status === "failed" && <button className="btn-ghost !py-1 !px-2 text-xs" onClick={() => retryRow(j.id)} data-testid={`recap-retry-${j.id.slice(0, 8)}`}><RotateCcw size={12} /> Retry</button>}</td>
                    </tr>
                  ))}
                  {jobs.length === 0 && <tr><td colSpan={6} className="p-6 text-center mono-label" style={{ color: "var(--taupe)" }}>No recap jobs yet</td></tr>}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel className="p-6" data-testid="recap-preview">
            <MonoLabel>Preview a member recap</MonoLabel>
            <select data-testid="recap-preview-select" className="input-x mt-3" value={pickMember} onChange={(e) => setPickMember(e.target.value)}>
              <option value="">Pick a member…</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.chapter}</option>)}
            </select>
            <button className="btn-red mt-3 w-full" onClick={runPreview} data-testid="recap-preview-btn"><PlayCircle size={14} /> Render preview</button>
            {preview && (
              <iframe title="Recap preview" data-testid="recap-preview-iframe"
                       srcDoc={preview}
                       className="mt-4 w-full h-[420px] hairline"
                       sandbox="allow-same-origin" />
            )}
          </Panel>
        </div>
      </div>
    </AdminShell>
  );
}
